import { EntityDocument, ManagementQuote } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInput } from '~/components/chat';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useDocumentSections } from '~/react-query-hooks/documents/useDocumentsEntity';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { DocumentViewer } from '~/views/alfred/document-search/document-viewer/DocumentViewer';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { ManagementCitations } from '~/widgets/citations-accordion-widget/components/management-citations/ManagementCitations';

import { EmptyState } from '../../empty-state/EmptyState';
import * as S from './ManagementQuotes.styles';

export interface ManagementQuotesProps {
  quotes: ManagementQuote[];
}

export const ManagementQuotes = ({ quotes }: ManagementQuotesProps) => {
  const { t } = useTranslation(['earnings', 'widget']);
  const [selectedDoc, setSelectedDoc] = useState<EntityDocument>();
  const [activeCitationIdx, setActiveCitationIdx] = useState<number>();
  const [isChatInputOpen, setIsChatInputOpen] = useState(false);
  const { navigateToAlfred } = useChatNavigation();

  const sectionIds = quotes.map(quote => quote.section_id);
  const { data } = useDocumentSections(sectionIds);

  if (!data?.length) {
    return <EmptyState />;
  }

  const citations = data
    // PLATFORM-3029 - Temporary roundabout logic to filter out citations without a speaker name
    .filter(section => section.tags?.speakerName)
    .map(section => {
      const quote = quotes.find(
        quote => quote.section_id === section.id
      ) as ManagementQuote;
      return {
        ...section,
        ...quote,
      };
    });

  const onClickCitation = (citationId: string, document: EntityDocument) => {
    const citationIdx = citations.findIndex(c => c.id === citationId);

    if (citationIdx === -1) {
      setActiveCitationIdx(undefined);
      setSelectedDoc(undefined);
      return;
    }

    setActiveCitationIdx(citationIdx);
    setSelectedDoc(document);
  };

  const onClose = () => {
    setSelectedDoc(undefined);
    setActiveCitationIdx(undefined);
  };

  const textFormatter = (text?: string) => {
    if (!text) {
      return '';
    }

    return text
      .trim()
      .replace(/(\*\*)(.*?)\1/g, '$2')
      .replace(/\s+/g, ' ');
  };

  const getHighlightTexts = () => {
    const citation =
      activeCitationIdx !== undefined ? citations[activeCitationIdx] : null;

    if (!selectedDoc || selectedDoc.kind === 'presentation') {
      return [];
    }

    return [
      {
        text: textFormatter(citation?.text),
        fallbackText: textFormatter(citation?.content),
        pages: undefined,
        isPrimary: true,
      },
    ];
  };

  const getChatProps = () => ({
    chatContext: {
      documents: [selectedDoc?.id] as string[],
    },
    config: {
      allowedModes: [],
      clearDocumentContextOnSubmit: true,
      disableContextHeader: true,
      persistInputTextOnSubmit: true,
      placeholder: t('widget:citations.askQuestionAbout', {
        documentName: selectedDoc?.name,
      }),
    },
    submitCallback: () =>
      navigateToAlfred('document', {
        trackingSource: POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
      }),
    trackingMetadata: {
      source: POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
    },
  });

  return (
    <div>
      <S.Title>
        {t('earningsInsight.drawer.earningsRecap.items.managementQuotes')}
      </S.Title>
      <S.Root>
        <ManagementCitations citations={citations} onClick={onClickCitation} />
      </S.Root>
      {!!selectedDoc && (
        <S.StyledDrawer isOpen handleCloseClick={onClose}>
          <ErrorBoundaryWrapper>
            <S.DrawerSectionWrapper>
              <S.DrawerSection>
                <S.CitationsHeaderTitle>
                  {t(
                    'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.citations'
                  )}
                </S.CitationsHeaderTitle>
                <ManagementCitations
                  citations={citations}
                  onClick={onClickCitation}
                  activeIdx={activeCitationIdx}
                />
              </S.DrawerSection>
              <S.DocumentChatButton
                iconName="Alfred"
                label={t('widget:citations.documentChat')}
                onClick={() => setIsChatInputOpen(true)}
              />
              <S.DrawerSection>
                {!!selectedDoc && (
                  <DocumentViewer
                    highlightTexts={getHighlightTexts()}
                    selectedCitation={{
                      document_id: selectedDoc.id,
                      document_name: selectedDoc.name,
                      document_type: selectedDoc.type,
                      pages: [],
                    }}
                  />
                )}
              </S.DrawerSection>
            </S.DrawerSectionWrapper>
          </ErrorBoundaryWrapper>
        </S.StyledDrawer>
      )}
      {selectedDoc && isChatInputOpen && (
        <S.DocumentChatModal
          isOpen={isChatInputOpen}
          onClose={() => setIsChatInputOpen(false)}
          title={t('widget:citations.documentChat')}
          description={<ChatInput {...getChatProps()} />}
        />
      )}
    </div>
  );
};
